/**
 * Settings Page
 */

import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { usePreferencesStore } from '@/store/usePreferencesStore';

export const SettingsPage: React.FC = () => {
  const theme = usePreferencesStore((s) => s.theme);
  const setTheme = usePreferencesStore((s) => s.setTheme);
  const fontSize = usePreferencesStore((s) => s.fontSize);
  const setFontSize = usePreferencesStore((s) => s.setFontSize);
  const autoRunPipeline = usePreferencesStore((s) => s.autoRunPipeline);
  const setAutoRunPipeline = usePreferencesStore((s) => s.setAutoRunPipeline);
  const autoCopyOutputToClipboard = usePreferencesStore((s) => s.autoCopyOutputToClipboard);
  const setAutoCopyOutputToClipboard = usePreferencesStore((s) => s.setAutoCopyOutputToClipboard);
  const showIoStats = usePreferencesStore((s) => s.showIoStats);
  const setShowIoStats = usePreferencesStore((s) => s.setShowIoStats);
  const showToolNameWithStepLabel = usePreferencesStore((s) => s.showToolNameWithStepLabel);
  const setShowToolNameWithStepLabel = usePreferencesStore((s) => s.setShowToolNameWithStepLabel);
  const autoVisualizers = usePreferencesStore((s) => s.autoVisualizers);
  const setAutoVisualizers = usePreferencesStore((s) => s.setAutoVisualizers);
  const showVisualizersInPipeline = usePreferencesStore((s) => s.showVisualizersInPipeline);
  const setShowVisualizersInPipeline = usePreferencesStore((s) => s.setShowVisualizersInPipeline);
  const resetToDefaults = usePreferencesStore((s) => s.resetToDefaults);

  return (
    <div className="h-full overflow-auto">
      <div className="p-8 max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">Home / Settings</div>
            <h1 className="text-3xl font-semibold mt-1">Application Settings</h1>
          </div>
        </div>

        <div className="mt-8 border border-border rounded-lg p-6 bg-muted/10">
          <div className="text-lg font-semibold">Appearance</div>
          <div className="mt-4 space-y-4">
            <SettingRow
              title="Color Scheme"
              description="Choose the application's appearance (dark mode, light mode, or system)."
              control={
                <div className="w-64">
                  <Select
                    value={theme}
                    onValueChange={(val) => setTheme(val as 'light' | 'dark' | 'system')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select theme" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="system">System</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="light">Light</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              }
            />
            <SettingRow
              title="Editor font size"
              description="Adjust the editor text size."
              control={
                <div className="w-64">
                  <Select
                    value={fontSize}
                    onValueChange={(val) => setFontSize(val as 'small' | 'medium' | 'large')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select font size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="large">Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              }
            />
          </div>

          <div className="mt-8 text-lg font-semibold">Automation</div>
          <div className="mt-4 space-y-4">
            <SettingRow
              title="Auto Run"
              description="Automatically run the pipeline when the input changes."
              control={<Switch checked={autoRunPipeline} onCheckedChange={setAutoRunPipeline} />}
            />
            <SettingRow
              title="Auto Clipboard"
              description="Automatically copy output to the clipboard after a run."
              control={
                <Switch
                  checked={autoCopyOutputToClipboard}
                  onCheckedChange={setAutoCopyOutputToClipboard}
                />
              }
            />
          </div>

          <div className="mt-8 text-lg font-semibold">Editor</div>
          <div className="mt-4 space-y-4">
            <SettingRow
              title="Auto Visualizers"
              description="Automatically show rich visualizers (tree view, table, diagram) based on detected content type. When off, the plain text editor is always used unless you explicitly add a visualizer."
              control={<Switch checked={autoVisualizers} onCheckedChange={setAutoVisualizers} />}
            />
            <SettingRow
              title="Show input/output stats"
              description="Display bytes, characters, and line count under the input and output editors."
              control={<Switch checked={showIoStats} onCheckedChange={setShowIoStats} />}
            />
            <SettingRow
              title="Show tool name with step label"
              description="When a pipeline step has a custom label, also show the underlying tool name (e.g., “My Label · JSON Format”)."
              control={
                <Switch
                  checked={showToolNameWithStepLabel}
                  onCheckedChange={setShowToolNameWithStepLabel}
                />
              }
            />
            <SettingRow
              title="Show visualizers in pipeline"
              description="When off, visualizer steps run but don't appear in the pipeline bar."
              control={
                <Switch
                  checked={showVisualizersInPipeline}
                  onCheckedChange={setShowVisualizersInPipeline}
                />
              }
            />
          </div>

          <div className="mt-8 flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={resetToDefaults}>
              Reset to Defaults
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingRow: React.FC<{
  title: string;
  description: string;
  control: React.ReactNode;
}> = ({ title, description, control }) => {
  return (
    <div className="flex items-center justify-between gap-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{description}</div>
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
};
